import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-admin-users',
  templateUrl: './admin-users.component.html',
  styleUrls: ['./admin-users.component.css']
})
export class AdminUsersComponent implements OnInit {
  users: any[] = [];
  filteredUsers: any[] = [];
  loading = true;
  error = '';
  searchTerm = '';
  roleFilter = '';
  confirmDeleteId: number | null = null;
  deleteSuccess = '';
  roleSuccess = '';
  roleError = '';

  editingRoleUserId: number | null = null;
  editingRole = '';

  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.loadUsers();
  }

  loadUsers(): void {
    this.loading = true;
    this.error = '';
    this.http.get<any[]>(`${this.apiUrl}Users`).subscribe({
      next: (data) => {
        this.users = data;
        this.applyFilters();
        this.loading = false;
      },
      error: () => {
        this.error = 'Failed to load users. Please check your connection.';
        this.loading = false;
      }
    });
  }

  applyFilters(): void {
    const term = this.searchTerm.toLowerCase();
    this.filteredUsers = this.users.filter(u => {
      const matchesSearch = !term ||
        (u.username || '').toLowerCase().includes(term) ||
        (u.email || '').toLowerCase().includes(term) ||
        (u.firstName || '').toLowerCase().includes(term) ||
        (u.lastName || '').toLowerCase().includes(term);
      const matchesRole = !this.roleFilter || u.role === this.roleFilter;
      return matchesSearch && matchesRole;
    });
  }

  onSearch(): void { this.applyFilters(); }

  onRoleFilter(role: string): void {
    this.roleFilter = role;
    this.applyFilters();
  }

  // ── Inline role editing ───────────────────────────────────────────────

  startEditRole(user: any): void {
    this.editingRoleUserId = user.id;
    this.editingRole = user.role || 'customer';
    this.roleError = '';
  }

  cancelRoleEdit(): void {
    this.editingRoleUserId = null;
    this.editingRole = '';
  }

  saveRole(user: any): void {
    if (!this.editingRole || this.editingRole === user.role) {
      this.cancelRoleEdit();
      return;
    }
    this.http.patch(`${this.apiUrl}Users/${user.id}/role`, { role: this.editingRole }).subscribe({
      next: (updated: any) => {
        user.role = updated.role ?? this.editingRole;
        this.applyFilters();
        this.cancelRoleEdit();
        this.roleSuccess = `${user.firstName || user.username}'s role updated to ${user.role}.`;
        setTimeout(() => this.roleSuccess = '', 3500);
      },
      error: () => {
        this.roleError = 'Failed to update role. Please try again.';
        setTimeout(() => this.roleError = '', 4000);
      }
    });
  }

  // ── Delete ────────────────────────────────────────────────────────────

  requestDelete(id: number): void { this.confirmDeleteId = id; }
  cancelDelete(): void { this.confirmDeleteId = null; }

  confirmDelete(): void {
    if (this.confirmDeleteId === null) return;
    this.http.delete(`${this.apiUrl}Users/${this.confirmDeleteId}`).subscribe({
      next: () => {
        this.users = this.users.filter(u => u.id !== this.confirmDeleteId);
        this.applyFilters();
        this.deleteSuccess = 'User deleted successfully.';
        this.confirmDeleteId = null;
        setTimeout(() => this.deleteSuccess = '', 3000);
      },
      error: () => {
        this.error = 'Failed to delete user.';
        this.confirmDeleteId = null;
      }
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────

  getRoleBadge(role: string): string {
    if (role === 'provider') return 'badge-provider';
    if (role === 'admin') return 'badge-admin';
    return 'badge-customer';
  }

  getRoleIcon(role: string): string {
    if (role === 'provider') return 'build';
    if (role === 'admin') return 'admin_panel_settings';
    return 'person';
  }

  getInitials(user: any): string {
    const f = (user.firstName || '').charAt(0).toUpperCase();
    const l = (user.lastName || '').charAt(0).toUpperCase();
    return (f + l) || (user.username || 'U').charAt(0).toUpperCase();
  }

  get customerCount(): number { return this.users.filter(u => u.role === 'customer').length; }
  get providerCount(): number { return this.users.filter(u => u.role === 'provider').length; }
  get adminCount(): number   { return this.users.filter(u => u.role === 'admin').length; }
}
